import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * A single pack card (e.g. Half Pack / Full Pack) inside a <pack-selector-component>.
 * Owns its own quantity stepper and recalculates its displayed price as
 * `base price x pieces x max(quantity, 1)` whenever the quantity or the
 * parent's base price (on variant change) updates.
 *
 * @typedef {Object} PackCardRefs
 * @property {HTMLInputElement} qtyInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 * @property {HTMLElement} priceAmount
 *
 * @extends {Component<PackCardRefs>}
 */
export class PackCardComponent extends Component {
  requiredRefs = ['qtyInput', 'minusButton', 'plusButton', 'priceAmount'];

  connectedCallback() {
    super.connectedCallback();
    this.#updateButtonState();
    this.classList.toggle('is-selected', this.getQty() > 0);
    // `pack-selector-component` is defined (and its own connectedCallback
    // run) after this class in the module, so on initial upgrade `selector`
    // isn't a live PackSelectorComponent yet here. Deferring to a microtask
    // lets both customElements.define() calls finish first.
    queueMicrotask(() => this.selector?.refreshSummary());
  }

  /** @returns {number} */
  get pieces() {
    return parseInt(this.dataset.pieces || '0', 10) || 0;
  }

  /** @returns {string} */
  get packName() {
    return this.dataset.packName || '';
  }

  /** @returns {PackSelectorComponent | null} */
  get selector() {
    return /** @type {PackSelectorComponent | null} */ (this.closest('pack-selector-component'));
  }

  /** @returns {number} */
  getQty() {
    return parseInt(this.refs.qtyInput.value, 10) || 0;
  }

  /** @param {Event} event */
  increase(event) {
    event.preventDefault();
    this.#setQty(this.getQty() + 1);
  }

  /** @param {Event} event */
  decrease(event) {
    event.preventDefault();
    this.#setQty(this.getQty() - 1);
  }

  /** @param {Event} event */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#setQty(parseInt(event.target.value, 10) || 0);
  }

  /** @param {FocusEvent} event */
  selectInputValue(event) {
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== this.refs.qtyInput) return;
    this.refs.qtyInput.select();
  }

  /**
      * Selects this card by clicking anywhere on it, but only while it's at 0 —
   * once a quantity is set, clicking the card again shouldn't reset it, and
   * the qty stepper/input already own their own interactions from there.
   */
  selectCard() {
    if (this.getQty() > 0) return;
    this.#setQty(1);
  }

  /**
   * Recalculates and redraws the price for this card using the parent's
   * current base price. Called on quantity changes and when the parent
   * reports a variant change.
   */
  updatePrice() {
    const selector = this.selector;
    if (!selector) return;

    const basePrice = Number(selector.dataset.basePrice) || 0;
    const qty = this.getQty();
    const multiplier = Math.max(qty, 1);
    const totalCents = basePrice * this.pieces * multiplier;

    const format = selector.dataset.moneyFormat || '{{amount}}';
    const currency = selector.dataset.currency || '';

    this.refs.priceAmount.textContent = formatMoney(totalCents, format, currency);
  }

  /** @param {number} value */
  #setQty(value) {
    const clamped = Math.max(0, Math.round(value) || 0);
    this.refs.qtyInput.value = String(clamped);

    this.updatePrice();
    this.#updateButtonState();
    this.classList.toggle('is-selected', clamped > 0);
    this.selector?.refreshSummary();
  }

  #updateButtonState() {
    this.refs.minusButton.disabled = this.getQty() <= 0;
  }
}

if (!customElements.get('pack-card-component')) {
  customElements.define('pack-card-component', PackCardComponent);
}

/**
 * Parent component for the pack selector block. Tracks the current
 * variant's base price (updating it when the shopper picks a different
 * variant elsewhere on the page) and keeps a live order summary — which
 * packs are selected, total pieces, and subtotal — in sync as pack card
 * quantities change. This component does not add anything to the cart
 * itself; it's a price/quantity preview only, but it does keep the page's
 * Add to Cart button(s) disabled while no pack is selected (see
 * #syncAddToCartButtons) since there'd otherwise be nothing valid to add.
 *
 * @typedef {Object} PackSelectorRefs
 * @property {HTMLElement} summary
 * @property {HTMLElement} summaryPacks
 * @property {HTMLElement} summaryPieces
 * @property {HTMLElement} summarySubtotal
 *
 * @extends {Component<PackSelectorRefs>}
 */
export class PackSelectorComponent extends Component {
  requiredRefs = ['summary', 'summaryPacks', 'summaryPieces', 'summarySubtotal'];
  #abortController = new AbortController();

  /**
   * Snapshot of the native quantity input's state from before this component
   * took it over, restored once every pack card is back to 0.
   * @type {{value: string, inputReadOnly: boolean, minusDisabled: boolean, plusDisabled: boolean} | null}
   */
  #nativeQuantitySnapshot = null;

  /** @type {MutationObserver | null} */
  #nativeQuantityObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // If the theme re-renders the native quantity selector (e.g. a section
    // morph after a variant change), re-apply the lock instead of silently
    // losing it.
    if (target) {
      this.#nativeQuantityObserver = new MutationObserver(() => {
        const totalPieces = this.packCards.reduce((sum, card) => sum + card.pieces * card.getQty(), 0);
        if (totalPieces > 0) this.#syncQuantity(totalPieces);
      });
      this.#nativeQuantityObserver.observe(target, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#nativeQuantityObserver?.disconnect();
    this.#syncQuantity(0);
  }

  /**
   * Finds the theme's native quantity selector inside this product's own
   * add-to-cart form (rendered by blocks/quantity.liquid via
   * blocks/buy-buttons.liquid), so it can be forced to match this block's
   * total piece count. Left untouched (the theme's own code isn't modified);
   * this only reads/writes its public refs from the outside. Returns null
   * when the merchant hasn't added/has hidden the quantity block.
   * @returns {import('@theme/component-quantity-selector').QuantitySelectorComponent | null}
   */
  #getNativeQuantityComponent() {
    const scope = this.closest('.shopify-section, dialog') || document;
    return /** @type {any} */ (scope.querySelector('product-form-component quantity-selector-component'));
  }

  /**
   * Finds this product's own add-to-cart form, regardless of whether the
   * theme's quantity block is present.
   * @returns {HTMLFormElement | null}
   */
  #getProductForm() {
    const scope = this.closest('.shopify-section, dialog') || document;
    return scope.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  }

  /**
   * Keeps the theme's add-to-cart form submitting the right quantity
   * whichever way the merchant has the "Quantity" block configured:
   * - If it's present (even if only the Add to Cart button is shown and
   *   quantity is otherwise hidden by the merchant), lock/sync it directly.
   * - If it isn't rendered on the page at all, fall back to a hidden
   *   `quantity` field appended to the form itself, so FormData still picks
   *   up the pack total on submit.
   * @param {number} totalPieces
   */
  #syncQuantity(totalPieces) {
    this.#syncAddToCartButtons(totalPieces);

    if (this.#getNativeQuantityComponent()) {
      this.#removeFallbackQuantityInput();
      this.#syncNativeQuantity(totalPieces);
    } else {
      this.#syncFallbackQuantity(totalPieces);
    }
  }

  /**
   * Removes the hidden fallback quantity field this component may have
   * injected, e.g. once the native quantity block becomes available again.
   */
  #removeFallbackQuantityInput() {
    this.#getProductForm()
      ?.querySelector('input[data-pack-selector-quantity]')
      ?.remove();
  }

  /**
   * Carries the total pieces via a hidden `quantity` input on the
   * add-to-cart form when no native quantity selector is rendered on the
   * page (e.g. the merchant only shows the Add to Cart button). Removes it
   * entirely once every pack card returns to 0 — at that point Add to Cart
   * is disabled anyway (see #syncAddToCartButtons), so there's nothing left
   * for this hidden field to carry.
   * @param {number} totalPieces
   */
  #syncFallbackQuantity(totalPieces) {
    const form = this.#getProductForm();
    if (!form) return;

    if (totalPieces > 0) {
      let input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[data-pack-selector-quantity]'));
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.setAttribute('data-pack-selector-quantity', '');
        form.appendChild(input);
      }
      input.value = String(totalPieces);
    } else {
      this.#removeFallbackQuantityInput();
    }
  }

  /**
   * Forces the theme's native quantity input to match the total pieces
   * selected across every pack card, and locks it so shoppers can't
   * override it by hand while a pack is selected. Restores its original
   * value and enabled state once every pack card returns to 0.
   *
   * Uses `readOnly`, not `disabled`, on the input itself: disabled form
   * fields are dropped from FormData entirely, which would submit no
   * quantity at all (Shopify then defaults to 1) when the theme's own Add
   * to Cart button is clicked. readOnly blocks editing but still submits.
   * @param {number} totalPieces
   */
  #syncNativeQuantity(totalPieces) {
    const qtyComponent = this.#getNativeQuantityComponent();
    const refs = qtyComponent?.refs;
    if (!qtyComponent || !refs?.quantityInput || !refs.minusButton || !refs.plusButton) return;

    const { quantityInput, minusButton, plusButton } = refs;

    if (totalPieces > 0) {
      if (!this.#nativeQuantitySnapshot) {
        this.#nativeQuantitySnapshot = {
          value: quantityInput.value,
          inputReadOnly: quantityInput.readOnly,
          minusDisabled: minusButton.disabled,
          plusDisabled: plusButton.disabled,
        };
      }

      quantityInput.value = String(totalPieces);
      quantityInput.readOnly = true;
      minusButton.disabled = true;
      plusButton.disabled = true;
      qtyComponent.classList.add('quantity-selector--locked');
    } else if (this.#nativeQuantitySnapshot) {
      quantityInput.value = this.#nativeQuantitySnapshot.value;
      quantityInput.readOnly = this.#nativeQuantitySnapshot.inputReadOnly;
      minusButton.disabled = this.#nativeQuantitySnapshot.minusDisabled;
      plusButton.disabled = this.#nativeQuantitySnapshot.plusDisabled;
      qtyComponent.classList.remove('quantity-selector--locked');
      this.#nativeQuantitySnapshot = null;
    }
  }

  /**
   * Finds this product's own Add to Cart button container(s), rendered by
   * blocks/add-to-cart.liquid via blocks/buy-buttons.liquid. Same scope and
   * lookup pattern as #getNativeQuantityComponent. Left untouched (the
   * theme's own code isn't modified); this only calls its public
   * enable()/disable() API from the outside.
   * @returns {import('@theme/product-form').AddToCartComponent[]}
   */
  #getAddToCartContainers() {
    const scope = this.closest('.shopify-section, dialog') || document;
    return /** @type {any} */ (Array.from(scope.querySelectorAll('product-form-component add-to-cart-component')));
  }

  /**
   * Keeps this product's Add to Cart button(s) disabled while no pack
   * (Half Pack/Full Pack) is selected, so a shopper can't submit the form
   * with nothing chosen. Combined with the variant's own availability (kept
   * in sync via #onProductSelect below) so a sold-out variant doesn't get
   * re-enabled just because a pack is selected.
   * @param {number} totalPieces
   */
  #syncAddToCartButtons(totalPieces) {
    const containers = this.#getAddToCartContainers();
    if (!containers.length) return;

    const variantAvailable = this.dataset.variantAvailable !== 'false';
    const shouldEnable = variantAvailable && totalPieces > 0;

    containers.forEach((container) => {
      if (shouldEnable) {
        container.enable?.();
      } else {
        container.disable?.();
      }
    });
  }

  /** @returns {PackCardComponent[]} */
  get packCards() {
    return /** @type {PackCardComponent[]} */ (Array.from(this.querySelectorAll('pack-card-component')));
  }

  /**
   * Recomputes and redraws the order summary from the current state of
   * every pack card. Hides the summary entirely when nothing is selected.
   */
  refreshSummary() {
    const selected = this.packCards.filter((card) => card.getQty() > 0);
    const totalPieces = this.packCards.reduce((sum, card) => sum + card.pieces * card.getQty(), 0);

    this.#syncQuantity(totalPieces);

    if (!selected.length) {
      this.refs.summary.hidden = true;
      return;
    }

    const basePrice = Number(this.dataset.basePrice) || 0;
    const piecesUnitText = this.dataset.piecesUnitText || 'pcs';

    const subtotalCents = selected.reduce((sum, card) => sum + basePrice * card.pieces * card.getQty(), 0);

    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || '';

    this.refs.summary.hidden = false;
    this.refs.summaryPacks.textContent = selected.map((card) => `${card.getQty()} × ${card.packName}`).join(' · ');
    this.refs.summaryPieces.textContent = `${totalPieces} ${piecesUnitText}`;
    this.refs.summarySubtotal.textContent = formatMoney(subtotalCents, format, currency);
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const resource = /** @type {any} */ (detail).resource;

        if (resource) {
          this.dataset.variantId = resource.id;
          this.dataset.basePrice = resource.price;
          this.dataset.variantAvailable = resource.available === false ? 'false' : 'true';
        } else {
          this.dataset.variantAvailable = 'false';
        }

        this.packCards.forEach((card) => card.updatePrice());
        this.refreshSummary();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pack-selector] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('pack-selector-component')) {
  customElements.define('pack-selector-component', PackSelectorComponent);
}
